import { Controller } from "@hotwired/stimulus"

// Occupancy map. "Arrange" toggles edit mode: unit cards drag (pointer events →
// mouse + touch) across a bounded, gridded, scrollable surface, snap to the grid
// on drop, and persist board_x/board_y via PATCH. Edge indicators count units
// that fall outside the viewport and scroll toward them on tap. Out of edit mode
// the cards are plain links. Unplaced units auto-flow server-side.
export default class extends Controller {
  static targets = [
    "canvas", "unit", "toggle", "toggleLabel", "hint", "scroll",
    "leftInd", "rightInd", "upInd", "downInd"
  ]
  static values = { cellWidth: Number, cellHeight: Number }

  connect() {
    this.editing = false
    this.drag = null
    requestAnimationFrame(() => this.updateIndicators())
  }

  toggle() {
    this.editing = !this.editing
    if (this.hasToggleLabelTarget) this.toggleLabelTarget.textContent = this.editing ? "Done" : "Arrange"
    if (this.hasToggleTarget) this.toggleTarget.classList.toggle("btn-primary", this.editing)
    if (this.hasHintTarget) this.hintTarget.classList.toggle("hidden", !this.editing)
    this.#paintGrid()
    this.unitTargets.forEach((u) => {
      u.style.cursor = this.editing ? "grab" : ""
      u.style.touchAction = this.editing ? "none" : ""
    })
  }

  #paintGrid() {
    if (!this.hasCanvasTarget) return
    const c = this.canvasTarget
    if (this.editing) {
      const line = "rgba(15,23,42,0.08)"
      c.style.backgroundImage =
        `linear-gradient(to right, ${line} 1px, transparent 1px),` +
        `linear-gradient(to bottom, ${line} 1px, transparent 1px)`
      c.style.backgroundSize = `${this.cellWidthValue}px ${this.cellHeightValue}px`
    } else {
      c.style.backgroundImage = ""
      c.style.backgroundSize = ""
    }
  }

  unitTargetConnected(el) {
    el.addEventListener("pointerdown", (e) => this.#start(e, el))
    el.addEventListener("pointermove", (e) => this.#move(e))
    el.addEventListener("pointerup", () => this.#end(el))
    el.addEventListener("pointercancel", () => this.#end(el))
    el.addEventListener("click", (e) => { if (this.editing) e.preventDefault() })
  }

  #start(e, el) {
    if (!this.editing) return
    e.preventDefault()
    const rect = el.getBoundingClientRect()
    this.drag = { el, dx: e.clientX - rect.left, dy: e.clientY - rect.top, moved: false }
    el.setPointerCapture(e.pointerId)
    el.style.zIndex = "50"
    el.style.cursor = "grabbing"
  }

  #move(e) {
    if (!this.drag) return
    const c = this.canvasTarget.getBoundingClientRect()
    const left = Math.max(0, e.clientX - c.left - this.drag.dx)
    const top = Math.max(0, e.clientY - c.top - this.drag.dy)
    this.drag.el.style.left = `${left}px`
    this.drag.el.style.top = `${top}px`
    this.drag.moved = true
  }

  #end(el) {
    if (!this.drag || this.drag.el !== el) return
    el.style.zIndex = ""
    el.style.cursor = "grab"
    const gx = Math.max(0, Math.round(parseFloat(el.style.left) / this.cellWidthValue))
    const gy = Math.max(0, Math.round(parseFloat(el.style.top) / this.cellHeightValue))
    el.style.left = `${gx * this.cellWidthValue}px`
    el.style.top = `${gy * this.cellHeightValue}px`
    const changed = String(gx) !== el.dataset.gx || String(gy) !== el.dataset.gy
    const wasMoved = this.drag.moved
    this.drag = null
    if (wasMoved && changed) {
      el.dataset.gx = gx
      el.dataset.gy = gy
      this.#persist(el, gx, gy)
    }
    this.updateIndicators()
  }

  #persist(el, gx, gy) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(el.dataset.repositionUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token || "",
        Accept: "application/json"
      },
      body: JSON.stringify({ unit: { board_x: gx, board_y: gy } })
    }).catch(() => {})
  }

  // Count units outside the scroll viewport on each edge; show/hide indicators.
  updateIndicators() {
    if (!this.hasScrollTarget) return
    const sc = this.scrollTarget
    const vl = sc.scrollLeft, vr = vl + sc.clientWidth
    const vt = sc.scrollTop, vb = vt + sc.clientHeight
    let left = 0, right = 0, up = 0, down = 0
    this.unitTargets.forEach((u) => {
      const x = parseFloat(u.style.left) || 0
      const y = parseFloat(u.style.top) || 0
      if (x + u.offsetWidth <= vl + 1) left++
      else if (x >= vr - 1) right++
      if (y + u.offsetHeight <= vt + 1) up++
      else if (y >= vb - 1) down++
    })
    this.#setInd(this.hasLeftIndTarget ? this.leftIndTarget : null, left)
    this.#setInd(this.hasRightIndTarget ? this.rightIndTarget : null, right)
    this.#setInd(this.hasUpIndTarget ? this.upIndTarget : null, up)
    this.#setInd(this.hasDownIndTarget ? this.downIndTarget : null, down)
  }

  #setInd(el, n) {
    if (!el) return
    el.classList.toggle("hidden", n === 0)
    el.classList.toggle("flex", n > 0)
    const count = el.querySelector("[data-count]")
    if (count) count.textContent = n
  }

  scrollLeft() { this.#nudge(-0.7, 0) }
  scrollRight() { this.#nudge(0.7, 0) }
  scrollUp() { this.#nudge(0, -0.7) }
  scrollDown() { this.#nudge(0, 0.7) }

  #nudge(fx, fy) {
    if (!this.hasScrollTarget) return
    this.scrollTarget.scrollBy({
      left: fx * this.scrollTarget.clientWidth,
      top: fy * this.scrollTarget.clientHeight,
      behavior: "smooth"
    })
  }
}
